import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  useQueryClient,
} from '@tanstack/react-query'
import { notification } from 'antd'
import { apiErrorToNotificationProps } from 'helpers/api-error-message'
import {
  bulkUpdateBenefits,
  batchUpdateBenefits,
  updateBenefit,
  fetchBenefitCarriers,
  fetchRenewalApplications,
  renewalBulkUpdate,
  RenewalBulkUpdateResponseDto,
} from '../methods'
import { Benefit } from 'types/benefit'
import {
  BulkUpdateBenefitRequestDto,
  UpdateBenefitRequestDto,
  BenefitQueryResultDto,
  BulkUpdateBenefitResponseDto,
  BatchUpdateBenefitRequestDto,
  BatchUpdateBenefitResponseDto,
} from '../dtos'
import { RenewalResponseDto } from '../dtos/Renewal.dto'
import { patchBenefitsInCache } from '../utils/benefitCacheUtils'

export function useBenefit() {
  const queryClient = useQueryClient()

  /**
   * Fetch renewal applications for a given client.
   * Results are cached for 30 minutes since the data changes infrequently.
   *
   * @example
   * const { queryRenewalApplications } = useBenefit()
   * const { data } = queryRenewalApplications(clientPublicKey)
   */
  const queryRenewalApplications = (
    clientPublicKey: string,
    options?: {
      queryOptions?: UseQueryOptions<RenewalResponseDto, Error>
      enabled?: boolean
    }
  ) => {
    return useQuery<RenewalResponseDto>({
      queryKey: ['renewal-applications', clientPublicKey],
      queryFn: () => fetchRenewalApplications(clientPublicKey),
      refetchOnWindowFocus: false,
      enabled: options?.enabled !== false && !!clientPublicKey,
      gcTime: 30 * 60 * 1000, // Cache renewal applications for 30 minutes since it doesn't change often
      ...options?.queryOptions,
    })
  }

  /**
   * Bulk update multiple benefits in a single request.
   * Shows a validation error notification when the server returns field-level errors,
   * or a generic error notification otherwise.
   *
   * @example
   * const { bulkUpdateBenefitMutation } = useBenefit()
   * const { mutateAsync } = bulkUpdateBenefitMutation()
   */
  const bulkUpdateBenefitMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      BulkUpdateBenefitResponseDto,
      Error,
      BulkUpdateBenefitRequestDto
    >
  }) => {
    return useMutation({
      mutationFn: (body: BulkUpdateBenefitRequestDto) =>
        bulkUpdateBenefits(body),

      onError(error: any) {
        notification.error(
          apiErrorToNotificationProps(error, 'Failed to update benefits')
        )
      },
      onSuccess(response: BulkUpdateBenefitResponseDto) {
        notification.success({
          message: `Successfully updated ${response.results.length} benefit(s)`,
        })
      },
      ...options?.mutationOptions,
    })
  }

  /**
   * Patch a single benefit by ID.
   *
   * @example
   * const { updateBenefitMutation } = useBenefit()
   * const { mutateAsync } = updateBenefitMutation()
   */
  const updateBenefitMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      BenefitQueryResultDto,
      Error,
      { benefitId: string; data: UpdateBenefitRequestDto }
    >
  }) => {
    const { onSuccess: callerOnSuccess, ...restMutationOptions } =
      options?.mutationOptions ?? {}

    return useMutation({
      mutationFn: ({
        benefitId,
        data,
      }: {
        benefitId: string
        data: UpdateBenefitRequestDto
      }) => updateBenefit(benefitId, data),

      onError(error: any) {
        notification.error(
          apiErrorToNotificationProps(error, 'Failed to update payment info')
        )
      },
      onSuccess(response: BenefitQueryResultDto, variables, context) {
        patchBenefitsInCache(
          queryClient,
          new Map([[variables.benefitId, variables.data]])
        )
        if (callerOnSuccess) {
          callerOnSuccess(response, variables, context)
        } else {
          notification.success({
            message: 'Benefit updated successfully',
          })
        }
      },
      ...restMutationOptions,
    })
  }

  /**
   * Fetch the list of available carrier names.
   *
   * @example
   * const { queryBenefitCarriers } = useBenefit()
   * const { data: carriers } = queryBenefitCarriers({ isActive: true, excludeMedicare: true })
   */
  const queryBenefitCarriers = (options?: {
    isActive?: boolean
    excludeMedicare?: boolean
    queryOptions?: UseQueryOptions<string[], Error>
    enabled?: boolean
  }) => {
    return useQuery<string[]>({
      queryKey: [
        'benefit-carriers',
        options?.isActive,
        options?.excludeMedicare,
      ],
      queryFn: () =>
        fetchBenefitCarriers(options?.isActive, options?.excludeMedicare),
      refetchOnWindowFocus: false,
      enabled: options?.enabled !== false,
      ...options?.queryOptions,
    })
  }

  /**
   * Bulk update renewal benefits, where each benefit carries its own payload.
   * Unlike `bulkUpdateBenefitMutation` which applies the same data to all selected benefits,
   * this sends individual payloads per benefit.
   * Automatically invalidates the `application-engine` query on success.
   *
   * Use `mutationOptions.onSuccess` / `onError` to attach per-invocation callbacks
   * without violating the Rules of Hooks.
   *
   * @example
   * const { renewalBulkUpdateMutation } = useBenefit()
   * const { mutateAsync } = renewalBulkUpdateMutation()
   */
  const renewalBulkUpdateMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      RenewalBulkUpdateResponseDto,
      Error,
      { benefits: Benefit[] }
    >
  }) => {
    const {
      onSuccess: callerOnSuccess,
      onError: callerOnError,
      ...restMutationOptions
    } = options?.mutationOptions ?? {}

    return useMutation({
      mutationFn: ({ benefits }: { benefits: Benefit[] }) =>
        renewalBulkUpdate(benefits),
      onError(error: any, variables, context) {
        notification.error(
          apiErrorToNotificationProps(
            error,
            'Failed to update renewal benefits'
          )
        )
        callerOnError?.(error, variables, context)
      },
      onSuccess(response: RenewalBulkUpdateResponseDto, variables, context) {
        notification.success({
          message: `Successfully updated ${response.successIds?.length ?? 0} renewal benefit(s)`,
        })
        queryClient.invalidateQueries({ queryKey: ['application-engine'] })
        callerOnSuccess?.(response, variables, context)
      },
      ...restMutationOptions,
    })
  }

  /**
   * Batch update benefits with per-row success/error reporting.
   * Shows a success notification for updated rows and a warning for failed rows,
   * so partial failures are surfaced without blocking the successful updates.
   *
   * @example
   * const { batchUpdateBenefitsMutation } = useBenefit()
   * const { mutateAsync } = batchUpdateBenefitsMutation()
   */
  const batchUpdateBenefitsMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      BatchUpdateBenefitResponseDto,
      Error,
      BatchUpdateBenefitRequestDto
    >
  }) => {
    return useMutation({
      mutationFn: (body: BatchUpdateBenefitRequestDto) =>
        batchUpdateBenefits(body),
      onError(error: any) {
        notification.error(
          apiErrorToNotificationProps(error, 'Failed to batch update benefits')
        )
      },
      onSuccess(response: BatchUpdateBenefitResponseDto) {
        if (response.results.length > 0) {
          notification.success({
            message: `Successfully updated ${response.results.length} benefit(s)`,
          })
        }
        if (response.errors.length > 0) {
          notification.warning({
            message: `${response.errors.length} benefit(s) failed to update`,
            description: response.errors
              .map((e) => `${e.benefitId}: ${e.message}`)
              .join('\n'),
          })
        }
      },
      ...options?.mutationOptions,
    })
  }

  return {
    queryRenewalApplications,
    bulkUpdateBenefitMutation,
    updateBenefitMutation,
    renewalBulkUpdateMutation,
    queryBenefitCarriers,
    batchUpdateBenefitsMutation,
  }
}
